import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import moment from 'moment';
import { LeaveStatusType } from '../../constants';
import { DD_MM_YYYY } from '../../pages/Tasks/TaskConstants';

const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];

const getStatusChipColor = (status) => {
  if (status === LeaveStatusType.APPROVED) return 'success';
  if (status === LeaveStatusType.REJECTED) return 'error';
  if (status === 'CANCELLED') return 'default';
  return 'warning';
};

/**
 * Employee or Senior Manager card for Additional WFH quota requests.
 * Shows existing quota, additional days requested, and resulting quarterly quota.
 */
const AdditionalWfhQuotaRequestCard = ({
  request,
  variant = 'employee',
  onCancel,
  onApprove,
  onReject,
}) => {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState(null);

  const isPending = request.status === LeaveStatusType.PENDING;
  const existing = request.existingQuotaDays ?? 0;
  const additional = request.additionalDays ?? request.additionalDaysRequested ?? 0;
  const resultingQuota = existing + additional;
  const quarterLabel = QUARTER_LABELS[request.quarterIndex] || `Q${(request.quarterIndex ?? 0) + 1}`;
  const requestDate = request.createdAt
    ? moment(request.createdAt).format(DD_MM_YYYY)
    : '-';

  const openConfirm = (action) => {
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (confirmAction === 'cancel' && onCancel) onCancel(request);
    if (confirmAction === 'approve' && onApprove) onApprove(request);
    if (confirmAction === 'reject' && onReject) onReject(request);
    setConfirmAction(null);
  };

  const confirmCopy = {
    cancel: {
      title: 'Cancel request?',
      body: 'This will cancel your Additional WFH quota request. Your current WFH quota will not change.',
    },
    approve: {
      title: 'Approve request?',
      body: `This will add ${additional} WFH day(s) to the employee's quarterly quota for ${quarterLabel} ${request.quotaYear}.`,
    },
    reject: {
      title: 'Reject request?',
      body: 'This will reject the Additional WFH quota request. Quota will not change.',
    },
  }[confirmAction || 'cancel'];

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            {variant === 'manager' && (
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {request.employeeName || request.employeeId}
                {request.employeeCode != null ? ` (${request.employeeCode})` : ''}
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {quarterLabel} {request.quotaYear}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <CalendarMonthRoundedIcon
                fontSize="inherit"
                sx={{ verticalAlign: 'middle', mr: 0.5 }}
              />
              Requested on {requestDate}
            </Typography>
          </Box>
          <Chip
            label={request.status}
            color={getStatusChipColor(request.status)}
            size="small"
            variant="outlined"
          />
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Existing quota: <strong>{existing}</strong> day(s) for this quarter
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Additional WFH requested:{' '}
            <strong>{additional}</strong> day(s)
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Resulting quota: <strong>{resultingQuota}</strong> day(s)
          </Typography>
          {request.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {request.description}
            </Typography>
          ) : null}
        </Box>

        {variant === 'employee' && isPending && onCancel && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button size="small" color="error" onClick={() => openConfirm('cancel')}>
              Cancel
            </Button>
          </Box>
        )}

        {variant === 'manager' && isPending && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={{ px: 3 }}
              onClick={() => openConfirm('reject')}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="success"
              sx={{ px: 3 }}
              onClick={() => openConfirm('approve')}
            >
              Approve
            </Button>
          </Box>
        )}
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{confirmCopy?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmCopy?.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>No</Button>
          <Button onClick={handleConfirm} autoFocus color="primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdditionalWfhQuotaRequestCard;
